import React, { useEffect, useState, useCallback } from 'react';
import { AlertTriangle, X, CreditCard } from 'lucide-react';
import { subscriptionsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const DISMISS_KEY_PREFIX = 'sub_warn_dismissed_';

/**
 * Banniere doree non-bloquante affichee dans les 7 jours avant expiration de l'abonnement.
 * - Verifie /api/subscription/my au mount + toutes les 30 min
 * - Le clic sur "Renouveler maintenant" declenche l'overlay de paiement (event 'subscription-expired')
 * - Peut etre fermee pour la journee (localStorage par date)
 */
export const SubscriptionExpiryWarning = () => {
  const { user, isAuthenticated } = useAuth();
  const [info, setInfo] = useState(null); // { daysLeft, endDate, planName }
  const [dismissed, setDismissed] = useState(false);

  const todayKey = () => DISMISS_KEY_PREFIX + new Date().toISOString().slice(0, 10);

  const fetchSubscription = useCallback(async () => {
    if (!isAuthenticated || !user) return;
    // Super-admins ne sont pas concernes
    if (user.role === 'super_admin') return;
    try {
      const r = await subscriptionsAPI.getMy();
      const agency = r.data?.agency;
      if (!agency || !agency.subscription_end_date) return;
      if (agency.subscription_status !== 'active') return; // l'overlay 402 prendra le relais

      const end = new Date(agency.subscription_end_date);
      const now = new Date();
      const msPerDay = 1000 * 60 * 60 * 24;
      const daysLeft = Math.ceil((end.getTime() - now.getTime()) / msPerDay);

      if (daysLeft >= 0 && daysLeft <= 7) {
        const plans = r.data?.plans || {};
        const plan = plans[agency.subscription_plan];
        setInfo({
          daysLeft,
          endDate: end,
          planName: plan?.name || agency.subscription_plan,
        });
      } else {
        setInfo(null);
      }
    } catch (e) {
      // 402 will be handled by overlay; silently ignore
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    setDismissed(localStorage.getItem(todayKey()) === '1');
  }, []);

  useEffect(() => {
    fetchSubscription();
    const interval = setInterval(fetchSubscription, 30 * 60 * 1000); // 30 min
    return () => clearInterval(interval);
  }, [fetchSubscription]);

  if (!info || dismissed) return null;

  const handleRenew = () => {
    // Reuse the same overlay used for expired subscriptions
    window.dispatchEvent(new CustomEvent('subscription-expired', {
      detail: { message: `Votre abonnement expire dans ${info.daysLeft} jour(s). Renouvelez maintenant pour eviter toute interruption.` },
    }));
  };

  const handleDismiss = () => {
    localStorage.setItem(todayKey(), '1');
    setDismissed(true);
  };

  // Tone : rouge si <=2 jours, jaune sinon
  const urgent = info.daysLeft <= 2;
  const bg = urgent
    ? 'bg-gradient-to-r from-red-600 to-red-700'
    : 'bg-gradient-to-r from-[#D4A745] to-[#b88a30]';

  const dayLabel = info.daysLeft <= 0
    ? "expire aujourd'hui"
    : info.daysLeft === 1
      ? 'expire demain'
      : `expire dans ${info.daysLeft} jours`;

  return (
    <div
      className={`${bg} text-white px-4 py-2 flex items-center justify-between gap-3 shadow-md sticky top-0 z-30`}
      data-testid="subscription-warning-banner"
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <AlertTriangle className="w-5 h-5 flex-shrink-0" />
        <div className="text-sm md:text-base flex-1 min-w-0">
          <span className="font-semibold">Votre abonnement {dayLabel}</span>
          <span className="hidden md:inline">
            {' '}({info.endDate.toLocaleDateString('fr-FR')}) - Renouvelez maintenant pour eviter toute interruption.
          </span>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={handleRenew}
          data-testid="renew-now-btn"
          className="bg-white text-[#1B3A4B] hover:bg-gray-100 px-3 py-1 rounded font-semibold text-sm flex items-center gap-1 transition"
        >
          <CreditCard className="w-4 h-4" />
          <span className="hidden sm:inline">Renouveler maintenant</span>
          <span className="sm:hidden">Renouveler</span>
        </button>
        <button
          onClick={handleDismiss}
          data-testid="dismiss-warning-btn"
          className="text-white/80 hover:text-white p-1 rounded"
          title="Masquer pour aujourd'hui"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default SubscriptionExpiryWarning;
